import { Link } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import { createContext, useContext, useEffect, useRef, useState } from "react";

// Context to coordinate dropdowns - only one open at a time
const NavDropdownContext = createContext<{
	openDropdown: string | null;
	openDropdownMenu: (label: string) => void;
	closeDropdownMenu: () => void;
} | null>(null);

export function NavDropdownProvider({ children }: { children: React.ReactNode }) {
	const [openDropdown, setOpenDropdown] = useState<string | null>(null);
	const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const openDropdownMenu = (label: string) => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
		setOpenDropdown(label);
	};

	const closeDropdownMenu = () => {
		closeTimeoutRef.current = setTimeout(() => {
			setOpenDropdown(null);
		}, 150);
	};

	useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
			}
		};
	}, []);

	return (
		<NavDropdownContext.Provider value={{ openDropdown, openDropdownMenu, closeDropdownMenu }}>
			{children}
		</NavDropdownContext.Provider>
	);
}

export interface NavDropdownItem {
	title: string;
	description: string;
	href: string;
	icon: React.ReactNode;
}

interface NavDropdownProps {
	label: string;
	items: NavDropdownItem[];
	isActive?: boolean;
}

export function NavDropdown({ label, items, isActive }: NavDropdownProps) {
	const context = useContext(NavDropdownContext);
	const containerRef = useRef<HTMLDivElement>(null);

	const isOpen = context ? context.openDropdown === label : false;

	const handleMouseEnter = () => {
		context?.openDropdownMenu(label);
	};

	const handleMouseLeave = () => {
		context?.closeDropdownMenu();
	};

	const handleClose = () => {
		context?.openDropdownMenu(""); // Close immediately on click
	};

	// Close on escape key
	useEffect(() => {
		function handleEscape(event: KeyboardEvent) {
			if (event.key === "Escape" && isOpen) {
				handleClose();
			}
		}

		document.addEventListener("keydown", handleEscape);
		return () => {
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isOpen]);

	return (
		<div
			ref={containerRef}
			className="relative"
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<button
				type="button"
				aria-expanded={isOpen}
				aria-haspopup="true"
				onClick={() => {
					if (!isOpen) {
						context?.openDropdownMenu(label);
					}
				}}
				className={`flex items-center gap-1 px-4 py-2 text-sm font-bold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
					isOpen
						? "text-primary bg-accent0"
						: isActive
							? "text-primary"
							: "text-foreground/80 hover:text-foreground hover:bg-muted0 dark:hover:bg-accent/50"
				}`}
			>
				{label}
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>expand_more</span>
			</button>

			<AnimatePresence mode="wait" initial={false}>
				{isOpen && (
					<m.div
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -6 }}
						transition={{ duration: 0.16, ease: [0.25, 0.1, 0.25, 1] }}
						className="absolute left-0 top-full mt-2 w-80 p-2 z-50"
						style={{ background: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)" }}
					>
						<div className="space-y-1">
							{items.map((item) => {
								return (
									<Link
										key={item.href}
										to={item.href}
										onClick={handleClose}
										className="flex items-start gap-3 p-3 hover:bg-muted0 dark:hover:bg-accent/50 transition-colors group"
									>
										<div className="w-10 h-10 bg-muted dark:bg-accent/50 flex items-center justify-center flex-shrink-0 group-hover:bg-accent0 transition-colors">
											{item.icon}
										</div>
										<div className="flex flex-col min-w-0">
											<span className="font-bold text-foreground text-sm">
												{item.title}
											</span>
											<span className="text-sm text-muted-foreground leading-snug">
												{item.description}
											</span>
										</div>
									</Link>
								);
							})}
						</div>
					</m.div>
				)}
			</AnimatePresence>
		</div>
	);
}

// Mobile version - Collapsible accordion
interface MobileNavSectionProps {
	label: string;
	items: NavDropdownItem[];
	onItemClick: () => void;
	icon: React.ReactNode;
}

export function MobileNavSection({
	label,
	items,
	onItemClick,
	icon,
}: MobileNavSectionProps) {
	const [isExpanded, setIsExpanded] = useState(false);

	return (
		<div>
			<button
				type="button"
				aria-expanded={isExpanded}
				aria-haspopup="true"
				onClick={() => setIsExpanded(!isExpanded)}
				className="flex items-center gap-3 w-full px-4 py-3 text-sm font-bold text-foreground hover:bg-muted0 dark:hover:bg-accent/50 transition-all duration-200 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
			>
				<div className="w-8 h-8 bg-muted0 dark:bg-accent/50 flex items-center justify-center group-hover:bg-accent0 transition-colors">
					{icon}
				</div>
				<span className="flex-1 text-left">{label}</span>
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>expand_more</span>
			</button>

			<AnimatePresence>
				{isExpanded && (
					<m.div
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: "auto", opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
						className="overflow-hidden"
					>
						<div className="px-2 pb-3 space-y-1">
							{items.map((item) => {
								return (
									<Link
										key={item.href}
										to={item.href}
										onClick={onItemClick}
										className="flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-200 text-foreground hover:bg-muted0 dark:hover:bg-accent/50 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
									>
										<div className="w-8 h-8 bg-muted0 dark:bg-accent/50 flex items-center justify-center group-hover:bg-accent0 transition-colors">
											{item.icon}
										</div>
										<div className="flex flex-col min-w-0">
											<span className="font-bold">{item.title}</span>
											<span className="text-xs text-muted-foreground">
												{item.description}
											</span>
										</div>
									</Link>
								);
							})}
						</div>
					</m.div>
				)}
			</AnimatePresence>
		</div>
	);
}
